import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  Chip,
  Divider,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  List,
  ListItem,
  ListItemText,
  Grid
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { apiFetch, getAuthenticatedApiUrl, openAuthenticatedUpload } from '../api/client';
import { useAuth } from '../contexts/AuthContext';

const PDF_JS_SCRIPT = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
const PDF_JS_WORKER = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';

const statusChipColor = {
  PENDENTE: 'warning',
  EM_ANDAMENTO: 'info',
  CONCLUIDO: 'success',
  CANCELADO: 'error',
  RECUSADO: 'error'
};

function carregarPdfJs() {
  if (window.pdfjsLib) {
    window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDF_JS_WORKER;
    return Promise.resolve(window.pdfjsLib);
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = PDF_JS_SCRIPT;
    script.async = true;
    script.onload = () => {
      if (window.pdfjsLib) {
        window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDF_JS_WORKER;
        resolve(window.pdfjsLib);
      } else {
        reject(new Error('Não foi possível carregar o PDF.js'));
      }
    };
    script.onerror = () => reject(new Error('Erro ao carregar biblioteca PDF.js'));
    document.body.appendChild(script);
  });
}

function SignaturePreview({ nome, cpf }) {
  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        boxSizing: 'border-box',
        px: 0.75,
        py: 0.4,
        borderRadius: 1,
        border: '1px dashed rgba(33, 150, 243, 0.9)',
        background: 'rgba(227,242,253,0.88)',
        fontFamily: 'Roboto, sans-serif',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        pointerEvents: 'none',
        userSelect: 'none'
      }}
    >
      <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, fontSize: 9, lineHeight: 1.15 }}>
        Assinado eletronicamente por:
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, fontSize: 10, lineHeight: 1.15 }}>
        {nome || 'Assinante'}
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', fontSize: 8, lineHeight: 1.1 }}>
        CPF: {cpf || '***.***.***-**'}
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', fontSize: 8, lineHeight: 1.1 }}>
        Data: {new Date().toLocaleString('pt-BR')}
      </Typography>
    </Box>
  );
}

const DEFAULT_SIG_W = 0.3;
const DEFAULT_SIG_H = 0.12;
const MIN_SIG_W = 0.14;
const MIN_SIG_H = 0.08;

export default function RegulamentoAssinaturasDocumentoDetalhePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, unidadeSelecionada, assinatura } = useAuth();
  const [documento, setDocumento] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [assinando, setAssinando] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState('');
  /** Caixa da assinatura: x/y = topo-esquerda (0–1), w/h fração da página */
  const [selectedBox, setSelectedBox] = useState(null);
  const [pdfReady, setPdfReady] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const pdfInstanceRef = useRef(null);
  const canvasRef = useRef(null);
  const overlayRef = useRef(null);
  const renderTaskRef = useRef(null);
  const renderGenRef = useRef(0);
  const dragRef = useRef(null);

  const carregarDocumento = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (unidadeSelecionada?.codigo) params.set('unidade', unidadeSelecionada.codigo);
      const qs = params.toString();
      const response = await apiFetch(
        qs ? `/api/assinaturas/documentos/${id}?${qs}` : `/api/assinaturas/documentos/${id}`
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.error || 'Erro ao carregar documento.');
      }
      setDocumento(data);
      setErro('');
    } catch (error) {
      console.error('Erro ao carregar documento:', error);
      setErro(error.message || 'Erro ao carregar documento.');
    } finally {
      setLoading(false);
    }
  }, [id, unidadeSelecionada?.codigo]);

  useEffect(() => {
    carregarDocumento();
  }, [carregarDocumento]);

  const participanteAtual = useMemo(() => {
    if (!documento || !user?.email) return null;
    return documento.participantes?.find(
      (participante) => participante.email?.toLowerCase() === user.email.toLowerCase()
    );
  }, [documento, user]);

  const anterioresAssinados = useMemo(() => {
    if (!documento || !participanteAtual) return false;
    return documento.participantes
      .filter((p) => p.ordem < participanteAtual.ordem)
      .every((p) => p.status === 'ASSINADO');
  }, [documento, participanteAtual]);

  const podeAssinar = useMemo(() => {
    if (!participanteAtual) return false;
    if (participanteAtual.status === 'ASSINADO') return false;
    if (documento?.status === 'CANCELADO') return false;
    return anterioresAssinados;
  }, [participanteAtual, anterioresAssinados, documento]);

  const cpfParaPreview =
    participanteAtual?.cpf ||
    assinatura?.cpf ||
    user?.assinatura_cpf ||
    user?.cpf ||
    '***.***.***-**';

  const resumo = useMemo(() => {
    if (!documento?.participantes) {
      return { total: 0, assinados: 0, pendentes: 0, proximo: null };
    }
    const total = documento.participantes.length;
    const assinados = documento.participantes.filter((p) => p.status === 'ASSINADO').length;
    const proximo = documento.participantes.find((p) => p.status !== 'ASSINADO') || null;
    return {
      total,
      assinados,
      pendentes: total - assinados,
      proximo
    };
  }, [documento]);

  const urlArquivoAutenticada = useCallback(() => {
    if (!id) return '';
    const params = new URLSearchParams();
    if (unidadeSelecionada?.codigo) params.set('unidade', unidadeSelecionada.codigo);
    const qs = params.toString();
    return getAuthenticatedApiUrl(
      qs
        ? `/api/assinaturas/documentos/${id}/arquivo?${qs}`
        : `/api/assinaturas/documentos/${id}/arquivo`
    );
  }, [id, unidadeSelecionada?.codigo]);

  const abrirPdfNovaAba = () => {
    const apiUrl = urlArquivoAutenticada();
    if (apiUrl) {
      window.open(apiUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    openAuthenticatedUpload(documento?.arquivoUrl || documento?.arquivoAtualPath);
  };

  const abrirViewer = async () => {
    if (!documento?.arquivoUrl && !documento?.arquivoAtualPath) {
      setSnackbar({ open: true, message: 'Arquivo do documento não está disponível.', severity: 'warning' });
      return;
    }
    try {
      setViewerOpen(true);
      setPdfLoading(true);
      setPdfError('');
      setSelectedBox(null);
      setPdfReady(false);
      setCurrentPage(1);
      const pdfjs = await carregarPdfJs();
      let data;
      const params = new URLSearchParams();
      if (unidadeSelecionada?.codigo) params.set('unidade', unidadeSelecionada.codigo);
      const qs = params.toString();
      const apiRes = await apiFetch(
        qs
          ? `/api/assinaturas/documentos/${id}/arquivo?${qs}`
          : `/api/assinaturas/documentos/${id}/arquivo`
      );
      if (apiRes.ok) {
        data = new Uint8Array(await apiRes.arrayBuffer());
      } else {
        const fallback = urlArquivoAutenticada();
        const fbRes = await fetch(fallback);
        if (!fbRes.ok) {
          throw new Error('Não autorizado a visualizar o PDF. Faça login novamente.');
        }
        data = new Uint8Array(await fbRes.arrayBuffer());
      }
      const loadingTask = pdfjs.getDocument({ data });
      const pdf = await loadingTask.promise;
      pdfInstanceRef.current = pdf;
      setTotalPages(pdf.numPages);
      setCurrentPage(1);
      setPdfReady(true);
      // render via useEffect (evita double render no mesmo canvas)
    } catch (error) {
      console.error('Erro ao preparar visualização do PDF:', error);
      setPdfError(error.message || 'Não foi possível carregar o PDF para visualização.');
    } finally {
      setPdfLoading(false);
    }
  };

  const cancelarRenderPdf = () => {
    if (renderTaskRef.current) {
      try {
        renderTaskRef.current.cancel();
      } catch (_) {
        /* ignore */
      }
      renderTaskRef.current = null;
    }
  };

  const renderPagina = async (pdf, pagina) => {
    if (!canvasRef.current || !pdf) return;
    cancelarRenderPdf();
    const gen = ++renderGenRef.current;
    setPdfLoading(true);
    setPdfError('');
    try {
      const page = await pdf.getPage(pagina);
      if (gen !== renderGenRef.current || !canvasRef.current) return;
      const scale = 1.3;
      const viewport = page.getViewport({ scale });
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      const task = page.render({ canvasContext: context, viewport });
      renderTaskRef.current = task;
      await task.promise;
      if (gen !== renderGenRef.current) return;
      renderTaskRef.current = null;
      setPdfLoading(false);
    } catch (error) {
      if (
        error?.name === 'RenderingCancelledException' ||
        /cancel/i.test(String(error?.message || ''))
      ) {
        return;
      }
      console.error('Erro ao renderizar página do PDF:', error);
      setPdfError(error.message || 'Erro ao renderizar a página.');
      setPdfLoading(false);
    }
  };

  const handleChangePage = (paginaNova) => {
    if (!pdfReady || !pdfInstanceRef.current) return;
    if (paginaNova < 1 || paginaNova > totalPages) return;
    if (paginaNova === currentPage) return;
    setCurrentPage(paginaNova);
  };

  useEffect(() => {
    if (!viewerOpen) {
      cancelarRenderPdf();
      return undefined;
    }
    if (!pdfReady || !pdfInstanceRef.current) return undefined;
    renderPagina(pdfInstanceRef.current, currentPage);
    return () => {
      cancelarRenderPdf();
    };
  }, [viewerOpen, pdfReady, currentPage]);

  const normFromEvent = (event) => {
    const overlay = overlayRef.current;
    if (!overlay) return null;
    const rect = overlay.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    return {
      x: Math.min(Math.max((event.clientX - rect.left) / rect.width, 0), 1),
      y: Math.min(Math.max((event.clientY - rect.top) / rect.height, 0), 1)
    };
  };

  const clampBox = (box) => {
    let { x, y, w, h } = box;
    w = Math.min(Math.max(w, MIN_SIG_W), 0.9);
    h = Math.min(Math.max(h, MIN_SIG_H), 0.5);
    x = Math.min(Math.max(x, 0), 1 - w);
    y = Math.min(Math.max(y, 0), 1 - h);
    return { x, y, w, h };
  };

  const handleOverlayClick = (event) => {
    if (pdfLoading || dragRef.current) return;
    const p = normFromEvent(event);
    if (!p) return;
    setSelectedBox(
      clampBox({
        x: p.x - DEFAULT_SIG_W / 2,
        y: p.y - DEFAULT_SIG_H / 2,
        w: DEFAULT_SIG_W,
        h: DEFAULT_SIG_H
      })
    );
  };

  const handleBoxPointerDown = (event) => {
    event.stopPropagation();
    event.preventDefault();
    if (pdfLoading || !selectedBox) return;
    const p = normFromEvent(event);
    if (!p) return;
    dragRef.current = {
      mode: 'move',
      startX: p.x,
      startY: p.y,
      orig: { ...selectedBox }
    };
  };

  const handleResizePointerDown = (event) => {
    event.stopPropagation();
    event.preventDefault();
    if (pdfLoading || !selectedBox) return;
    const p = normFromEvent(event);
    if (!p) return;
    dragRef.current = {
      mode: 'resize',
      startX: p.x,
      startY: p.y,
      orig: { ...selectedBox }
    };
  };

  useEffect(() => {
    if (!viewerOpen) return undefined;
    const onMove = (event) => {
      const drag = dragRef.current;
      if (!drag) return;
      const p = normFromEvent(event);
      if (!p) return;
      if (drag.mode === 'move') {
        const dx = p.x - drag.startX;
        const dy = p.y - drag.startY;
        setSelectedBox(
          clampBox({
            x: drag.orig.x + dx,
            y: drag.orig.y + dy,
            w: drag.orig.w,
            h: drag.orig.h
          })
        );
      } else if (drag.mode === 'resize') {
        setSelectedBox(
          clampBox({
            x: drag.orig.x,
            y: drag.orig.y,
            w: drag.orig.w + (p.x - drag.startX),
            h: drag.orig.h + (p.y - drag.startY)
          })
        );
      }
    };
    const onUp = () => {
      if (!dragRef.current) return;
      dragRef.current = { ...dragRef.current, ending: true };
      setTimeout(() => {
        dragRef.current = null;
      }, 50);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [viewerOpen]);

  const handleAssinarDocumento = async () => {
    if (!selectedBox) {
      setSnackbar({ open: true, message: 'Posicione e ajuste a caixa da assinatura no documento.', severity: 'warning' });
      return;
    }
    try {
      setAssinando(true);
      const response = await apiFetch(`/api/assinaturas/documentos/${id}/assinar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pagina: currentPage,
          posX: selectedBox.x,
          posY: selectedBox.y,
          largura: selectedBox.w,
          altura: selectedBox.h,
          usuarioEmail: user?.email
        })
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.error || 'Erro ao registrar assinatura.');
      }
      setSnackbar({
        open: true,
        message: data.proximoAssinaturaDocumentoId
          ? 'Avaliação assinada. Agora assine o processo de pagamento.'
          : data.proximoSignatarioNotificado
            ? 'Assinado. O próximo da cadeia foi notificado por e-mail.'
            : 'Documento assinado com sucesso!',
        severity: 'success',
      });
      setViewerOpen(false);
      setSelectedBox(null);
      await carregarDocumento();
      if (documento?.processoId) {
        window.dispatchEvent(
          new CustomEvent('contratos:refresh', {
            detail: { source: 'assinaturas', tipo: 'assinatura_registrada', processoId: documento.processoId }
          })
        );
      }
      const proximoId = data.proximoAssinaturaDocumentoId || location.state?.proximoAssinaturaDocumentoId;
      if (proximoId && String(proximoId) !== String(id)) {
        navigate(`/assinaturas/documentos/${proximoId}`, { replace: true, state: {} });
      }
    } catch (error) {
      console.error('Erro ao assinar documento:', error);
      setSnackbar({ open: true, message: error.message || 'Erro ao assinar documento.', severity: 'error' });
    } finally {
      setAssinando(false);
    }
  };

  const handleRecusar = async () => {
    try {
      setAssinando(true);
      const body = { acao: 'RECUSAR', usuarioEmail: user?.email };
      if (unidadeSelecionada?.codigo) body.unidade = unidadeSelecionada.codigo;
      const response = await apiFetch(`/api/assinaturas/documentos/${id}/assinar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.error || 'Erro ao recusar assinatura.');
      }
      setSnackbar({ open: true, message: 'Assinatura recusada.', severity: 'info' });
      setViewerOpen(false);
      await carregarDocumento();
      if (documento?.processoId) {
        window.dispatchEvent(
          new CustomEvent('contratos:refresh', {
            detail: { source: 'assinaturas', tipo: 'assinatura_recusada', processoId: documento.processoId }
          })
        );
      }
    } catch (error) {
      console.error('Erro ao recusar assinatura:', error);
      setSnackbar({ open: true, message: error.message || 'Erro ao recusar assinatura.', severity: 'error' });
    } finally {
      setAssinando(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (erro) {
    return (
      <Box sx={{ p: 3 }}>
        <Stack spacing={2} alignItems="flex-start">
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
            Voltar
          </Button>
          <Alert severity="error">{erro}</Alert>
          <Button variant="contained" onClick={carregarDocumento} startIcon={<RefreshIcon />}>
            Tentar novamente
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
            Voltar
          </Button>
          <Typography variant="h4" fontWeight={700} color="primary">
            {documento?.titulo}
          </Typography>
          <Chip
            label={documento?.status || '—'}
            color={statusChipColor[documento?.status] || 'default'}
          />
          {documento?.processoId && (
            <Chip
              size="small"
              variant="outlined"
              color="primary"
              label={`Processo #${documento.processoId}`}
              onClick={() =>
                navigate('/contratos/atividades', {
                  state: { highlightProcessoId: documento.processoId },
                })
              }
            />
          )}
          {documento?.atividadeId && (
            <Chip size="small" variant="outlined" label={`Atividade #${documento.atividadeId}`} />
          )}
        </Stack>

        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid rgba(25,118,210,0.12)' }}>
              <Stack spacing={2}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Informações do documento
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
                  <Button
                    variant="outlined"
                    startIcon={<PictureAsPdfIcon />}
                    onClick={abrirPdfNovaAba}
                    disabled={!documento?.arquivoUrl && !documento?.arquivoAtualPath}
                  >
                    Abrir PDF
                  </Button>
                  {podeAssinar && (
                    <Stack direction="row" spacing={1}>
                      <Button variant="contained" onClick={abrirViewer} sx={{ textTransform: 'none' }}>
                        Assinar documento
                      </Button>
                      <Button variant="text" color="error" onClick={handleRecusar} disabled={assinando}>
                        Recusar assinatura
                      </Button>
                    </Stack>
                  )}
                </Stack>
                <Divider />
                <Stack spacing={0.5}>
                  <Typography variant="body2" color="text.secondary">
                    Criado por: <strong>{documento?.criadoPorNome || documento?.criadoPor?.nome || '—'}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Criado em: {documento?.criadoEm ? new Date(documento.criadoEm).toLocaleString('pt-BR') : '—'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Atualizado em: {documento?.atualizadoEm ? new Date(documento.atualizadoEm).toLocaleString('pt-BR') : '—'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Concluído em: {documento?.concluidoEm ? new Date(documento.concluidoEm).toLocaleString('pt-BR') : '—'}
                  </Typography>
                </Stack>
                {documento?.descricao && (
                  <Alert severity="info" icon={false} sx={{ backgroundColor: 'rgba(227,242,253,0.5)' }}>
                    {documento.descricao}
                  </Alert>
                )}
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid rgba(25,118,210,0.12)' }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Resumo
              </Typography>
              <Stack spacing={1}>
                <Typography variant="body2" color="text.secondary">
                  Assinantes: <strong>{resumo.total || 0}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Concluídos: <strong>{resumo.assinados || 0}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Pendentes: <strong>{(resumo.total || 0) - (resumo.assinados || 0)}</strong>
                </Typography>
              </Stack>
            </Paper>
          </Grid>
        </Grid>

        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid rgba(25,118,210,0.12)' }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Participantes
          </Typography>
          <Stack spacing={1.5}>
            {documento?.participantes?.map((participante) => (
              <Paper
                key={participante.id}
                variant="outlined"
                sx={{
                  p: 2,
                  borderColor: participante.status === 'ASSINADO' ? 'rgba(46,125,50,0.4)' : 'rgba(25,118,210,0.16)',
                  background:
                    participante.email?.toLowerCase() === user?.email?.toLowerCase()
                      ? 'rgba(227,242,253,0.6)'
                      : 'white'
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {participante.ordem}. {participante.nome}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {participante.email}
                      {participante.cpf ? ` • ${participante.cpf}` : ''}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      label={participante.status || 'PENDENTE'}
                      color={statusChipColor[participante.status] || 'default'}
                      size="small"
                    />
                    {participante.assinouEm && (
                      <Typography variant="caption" color="text.secondary">
                        {new Date(participante.assinouEm).toLocaleString('pt-BR')}
                      </Typography>
                    )}
                  </Stack>
                </Stack>
              </Paper>
            ))}
          </Stack>
        </Paper>

        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid rgba(25,118,210,0.12)' }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Eventos
          </Typography>
          {documento?.eventos?.length ? (
            <List dense>
              {documento.eventos.map((evento) => (
                <ListItem key={evento.id} sx={{ px: 0 }}>
                  <ListItemText
                    primary={evento.descricao || evento.tipo}
                    secondary={
                      evento.createdAt
                        ? `${new Date(evento.createdAt).toLocaleString('pt-BR')} • ${evento.usuarioNome || evento.usuarioEmail || 'Sistema'}`
                        : evento.usuarioNome || evento.usuarioEmail || 'Sistema'
                    }
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Nenhum evento registrado até o momento.
            </Typography>
          )}
        </Paper>
      </Stack>

      <Dialog open={viewerOpen} onClose={() => setViewerOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Assinar documento</Typography>
          <IconButton onClick={() => setViewerOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ backgroundColor: '#f5f7fb' }}>
          {pdfError ? (
            <Alert severity="error">{pdfError}</Alert>
          ) : (
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                Clique para posicionar. Arraste a caixa e use o canto inferior direito para redimensionar. A assinatura
                fica exatamente nesse lugar no PDF.
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                <IconButton onClick={() => handleChangePage(currentPage - 1)} disabled={currentPage <= 1}>
                  <NavigateBeforeIcon />
                </IconButton>
                <Typography variant="body2">
                  Página {currentPage} / {totalPages}
                </Typography>
                <IconButton onClick={() => handleChangePage(currentPage + 1)} disabled={currentPage >= totalPages}>
                  <NavigateNextIcon />
                </IconButton>
              </Stack>
              <Box
                sx={{
                  position: 'relative',
                  borderRadius: 2,
                  overflow: 'hidden',
                  boxShadow: '0 12px 32px rgba(13, 71, 161, 0.12)',
                  border: '1px solid rgba(25,118,210,0.16)',
                  backgroundColor: 'white'
                }}
              >
                <canvas ref={canvasRef} style={{ width: '100%', display: 'block' }} />
                <Box
                  ref={overlayRef}
                  onClick={handleOverlayClick}
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    cursor: 'crosshair',
                    '&:hover': { boxShadow: 'inset 0 0 0 2px rgba(25,118,210,0.2)' }
                  }}
                >
                  {selectedBox && (
                    <Box
                      onPointerDown={handleBoxPointerDown}
                      sx={{
                        position: 'absolute',
                        top: `${selectedBox.y * 100}%`,
                        left: `${selectedBox.x * 100}%`,
                        width: `${selectedBox.w * 100}%`,
                        height: `${selectedBox.h * 100}%`,
                        cursor: 'move',
                        zIndex: 2,
                        boxShadow: '0 0 0 1px rgba(25,118,210,0.45)'
                      }}
                    >
                      <SignaturePreview
                        nome={participanteAtual?.nome || user?.nome || 'Assinante'}
                        cpf={cpfParaPreview}
                      />
                      <Box
                        onPointerDown={handleResizePointerDown}
                        sx={{
                          position: 'absolute',
                          right: -5,
                          bottom: -5,
                          width: 14,
                          height: 14,
                          borderRadius: '2px',
                          bgcolor: 'primary.main',
                          border: '2px solid #fff',
                          cursor: 'nwse-resize',
                          zIndex: 3,
                          pointerEvents: 'auto'
                        }}
                      />
                    </Box>
                  )}
                </Box>
                {pdfLoading && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'rgba(255,255,255,0.6)'
                    }}
                  >
                    <CircularProgress size={32} />
                  </Box>
                )}
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewerOpen(false)} disabled={assinando}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleAssinarDocumento}
            disabled={assinando || !selectedBox || pdfLoading}
          >
            {assinando ? 'Registrando...' : 'Confirmar assinatura'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
